import { useCallback, useContext } from 'react';

import { useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import {
  qrCodeDataAtom,
  qrCodeErrorAtom,
  qrCodeLoadingAtom,
  showQRCodeAtom,
} from '@/modules/annotator/multiFileViewer/imageToolbar/atoms';
import { endpoints } from '@/react-query/endpoints';
import { makeAPIRequestV2 } from '@/utils/api';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { usePatientDownloadableSettings } from './usePatientDownloadableSettings';
import { usePatientPrintoutData } from './usePatientPrintoutData';

export const useGenerateQRCode = () => {
  const externalPatientContext = useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const patientId = selectedPatientId ?? externalPatientContext.patientId;
  const settings = usePatientDownloadableSettings();
  const { trackEvent } = useTrackEvent();

  const setQrCodeData = useSetAtom(qrCodeDataAtom);
  const setQrCodeLoading = useSetAtom(qrCodeLoadingAtom);
  const setShowQRCode = useSetAtom(showQRCodeAtom);
  const setQrCodeError = useSetAtom(qrCodeErrorAtom);

  const { visitIdToImagesMap, mixPanelData } = usePatientPrintoutData();

  const generateQRCode = useCallback(async () => {
    if (!patientId || !visitIdToImagesMap.length) {
      console.error('Missing required data for QR code generation');
      return null;
    }

    setQrCodeLoading(true);
    try {
      const response = await makeAPIRequestV2(
        `/${endpoints.v3}/${endpoints.patient}/${endpoints.patientDownloadable}`,
        {
          data: {
            patientId,
            ...settings,
          },
        },
        'POST',
      );

      const data = response.data as {
        link: string;
        qrCode: string;
        accessCode: number | null;
      };

      setQrCodeData(data);
      setShowQRCode(true);
      setQrCodeError(null);

      // Track the QR code generation event
      trackEvent(mixpanelEvents.previewDialogQRCodeGenerated, {
        patientId,
        ...mixPanelData,
        externalPatientId: externalPatientContext.externalPatientId,
        externalImageId: externalPatientContext.externalImageId,
        isIframe: !!externalPatientContext.isIframe,
        trialStatus: externalPatientContext.trialStatus,
        centralizedBilling: externalPatientContext.centralizedBilling,
      });

      return data;
    } catch (error) {
      setQrCodeError(
        error instanceof Error ? error.message : 'Failed to generate QR code',
      );
      setShowQRCode(true);
    } finally {
      setQrCodeLoading(false);
    }
  }, [
    externalPatientContext.centralizedBilling,
    externalPatientContext.externalImageId,
    externalPatientContext.externalPatientId,
    externalPatientContext.isIframe,
    externalPatientContext.trialStatus,
    mixPanelData,
    patientId,
    setQrCodeData,
    setQrCodeError,
    setQrCodeLoading,
    setShowQRCode,
    settings,
    trackEvent,
    visitIdToImagesMap,
  ]);

  return { generateQRCode };
};
